import { z } from "zod";

/**
 * The direct path is only a data-plane optimization. The authenticated Rivets
 * relay remains the control plane and is always the fallback.
 */
export const DIRECT_TRANSPORT_PROTOCOL_VERSION = 1;
export const DIRECT_TICKET_TTL_MS = 60_000;
/** A direct socket is deliberately short-lived. Clients renew it through the
 * central control plane, so session/device/org revocation can retain shell
 * access for at most one minute even when the Tailscale path is otherwise
 * healthy. The relay stays attached while renewal occurs. */
export const DIRECT_SESSION_TTL_MS = 60_000;
export const DIRECT_HANDSHAKE_TIMEOUT_MS = 10_000;
export const DIRECT_IDLE_TIMEOUT_MS = 5 * 60_000;
export const DIRECT_MAX_MESSAGE_BYTES = 256 * 1024;
export const DIRECT_MAX_CONNECTIONS = 16;
/** Per-connection ceiling on client frames. A same-machine terminal sees key
 * repeat plus output acks on one socket, and 100/s was close enough to trip on
 * a fast scroll; 300/s is still a small bound for a loopback-only listener. */
export const DIRECT_MESSAGES_PER_SECOND = 300;
export const DIRECT_FLOW_WINDOW_BYTES = 256 * 1024;

const Id = z.string().min(1).max(160).regex(/^[A-Za-z0-9._:-]+$/);
const WorkerThumbprint = z.string().min(32).max(128).regex(/^[A-Za-z0-9_-]+$/);

export const DirectTransportFeature = z.enum(["terminal", "preview", "file"]);
export type DirectTransportFeature = z.infer<typeof DirectTransportFeature>;

export const DirectTicketScope = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("terminal"), terminalId: Id }),
  z.object({ kind: z.literal("preview"), serviceId: Id }),
  z.object({
    kind: z.literal("file"),
    /** POSIX-style, worktree-relative path. Absolute paths and traversal are invalid. */
    path: z.string().min(1).max(4_096).refine(isSafeWorktreeRelativePath, "unsafe worktree-relative path"),
    access: z.enum(["read", "write"]),
  }),
]);
export type DirectTicketScope = z.infer<typeof DirectTicketScope>;

export const DirectTicketRequest = z.object({
  workerId: Id,
  worktreeId: Id,
  audience: z.string().url().max(2_048).refine(isDirectAudience, "audience must be a Tailscale HTTPS or loopback direct endpoint"),
  scope: DirectTicketScope,
});
export type DirectTicketRequest = z.infer<typeof DirectTicketRequest>;

export const DirectTicketResponse = z.object({
  ticketId: Id,
  ticket: z.string().min(40).max(256).regex(/^rvd_[A-Za-z0-9_-]+$/),
  expiresAt: z.number().int().positive(),
  audience: z.string().url(),
  workerThumbprint: WorkerThumbprint,
  scope: DirectTicketScope,
});
export type DirectTicketResponse = z.infer<typeof DirectTicketResponse>;

export const DirectTicketConsumeRequest = z.object({
  ticket: z.string().min(40).max(256).regex(/^rvd_[A-Za-z0-9_-]+$/),
  audience: z.string().url().max(2_048).refine(isDirectAudience),
});
export type DirectTicketConsumeRequest = z.infer<typeof DirectTicketConsumeRequest>;

export const DirectTicketGrant = z.object({
  ticketId: Id,
  orgId: Id,
  userId: z.string().min(1).max(160),
  workerId: Id,
  worktreeId: Id,
  audience: z.string().url(),
  workerThumbprint: WorkerThumbprint,
  scope: DirectTicketScope,
  expiresAt: z.number().int().positive(),
});
export type DirectTicketGrant = z.infer<typeof DirectTicketGrant>;

const RelayFallback = z.object({
  kind: z.literal("relay"),
  available: z.literal(true),
});

const DirectDescriptorBase = z.object({
  transport: z.literal("tailscale"),
  protocolVersion: z.literal(DIRECT_TRANSPORT_PROTOCOL_VERSION),
  features: z.array(DirectTransportFeature).max(3),
  fallback: RelayFallback,
});

export const DirectTransportDescriptor = z.discriminatedUnion("state", [
  DirectDescriptorBase.extend({ state: z.literal("disabled") }),
  DirectDescriptorBase.extend({
    state: z.literal("unsupported"),
    reason: z.enum(["not_installed", "not_running", "not_logged_in", "https_unavailable", "platform"]),
  }),
  DirectDescriptorBase.extend({ state: z.literal("starting") }),
  DirectDescriptorBase.extend({
    state: z.literal("running"),
    endpoint: z.string().url().refine(isTailscaleAudience),
    audience: z.string().url().refine(isTailscaleAudience),
    workerThumbprint: WorkerThumbprint,
  }),
  DirectDescriptorBase.extend({
    state: z.literal("failed"),
    code: z.enum(["discovery_failed", "serve_failed", "gateway_failed", "configuration_changed"]),
    retryable: z.boolean(),
  }),
]);
export type DirectTransportDescriptor = z.infer<typeof DirectTransportDescriptor>;

/** Random per-machine identity a worker advertises on its loopback descriptor
 * and a client on the same machine can read from disk. Matching it is how a
 * client knows `127.0.0.1` means *this* worker rather than any worker. */
export const HostId = z.string().regex(/^[A-Za-z0-9_-]{22,64}$/);
export type HostId = z.infer<typeof HostId>;

/** The LocalApi data plane (`packages/core/src/local-api.ts`) this daemon
 * serves version 1 of. */
export const LOCAL_API_VERSION = 1;
export const LOCAL_API_BASE_PATH = "/v1/local";

/** Advertised on every `running` loopback descriptor: the daemon serves
 * LocalApi on the same listener. */
export const LocalApiAdvertisement = z.object({
  version: z.literal(LOCAL_API_VERSION),
  basePath: z.literal(LOCAL_API_BASE_PATH),
});
export type LocalApiAdvertisement = z.infer<typeof LocalApiAdvertisement>;

const LoopbackDescriptorBase = z.object({
  transport: z.literal("loopback"),
  protocolVersion: z.literal(DIRECT_TRANSPORT_PROTOCOL_VERSION),
  features: z.array(DirectTransportFeature).max(3),
  fallback: RelayFallback,
});

/**
 * The same gateway the Tailscale path fronts, reached over plain loopback by a
 * client on the worker's own machine. It carries no authority of its own: the
 * first frame is still an API-issued, worker-redeemed ticket, and the relay
 * stays attached as control plane and fallback.
 */
export const LoopbackTransportDescriptor = z.discriminatedUnion("state", [
  LoopbackDescriptorBase.extend({ state: z.literal("disabled") }),
  LoopbackDescriptorBase.extend({
    state: z.literal("running"),
    endpoint: z.string().url().refine(isLoopbackAudience),
    audience: z.string().url().refine(isLoopbackAudience),
    workerThumbprint: WorkerThumbprint,
    hostId: HostId,
    /** The LocalApi served on this listener. */
    localApi: LocalApiAdvertisement,
    /** Absolute directory the daemon keeps worktrees under. Advisory only: a
     * same-machine client may read files there directly, but LocalApi and the
     * relay stay authoritative. */
    worktreesRoot: z.string().startsWith("/").max(4_096),
  }),
  LoopbackDescriptorBase.extend({
    state: z.literal("failed"),
    code: z.enum(["gateway_failed"]),
    retryable: z.boolean(),
  }),
]).superRefine((descriptor, context) => {
  if (descriptor.state === "running" && descriptor.endpoint !== descriptor.audience) {
    context.addIssue({
      code: "custom",
      path: ["audience"],
      message: "loopback endpoint and audience must match",
    });
  }
});
export type LoopbackTransportDescriptor = z.infer<typeof LoopbackTransportDescriptor>;

export const DirectClientHello = z.object({
  type: z.literal("direct.hello"),
  version: z.literal(DIRECT_TRANSPORT_PROTOCOL_VERSION),
  ticket: z.string().min(40).max(256).regex(/^rvd_[A-Za-z0-9_-]+$/),
  features: z.array(DirectTransportFeature).min(1).max(3),
});
export type DirectClientHello = z.infer<typeof DirectClientHello>;

export const DirectServerHello = z.discriminatedUnion("accepted", [
  z.object({
    type: z.literal("direct.hello.result"),
    accepted: z.literal(true),
    version: z.literal(DIRECT_TRANSPORT_PROTOCOL_VERSION),
    feature: DirectTransportFeature,
    expiresAt: z.number().int().positive(),
  }),
  z.object({
    type: z.literal("direct.hello.result"),
    accepted: z.literal(false),
    code: z.enum(["invalid_ticket", "expired", "scope_mismatch", "version", "capacity", "rate_limited"]),
    fallback: RelayFallback,
  }),
]);
export type DirectServerHello = z.infer<typeof DirectServerHello>;

const FrameBase = z.object({
  channelId: Id,
  sequence: z.number().int().nonnegative(),
});
const BoundedData = z.string().max(Math.ceil(DIRECT_MAX_MESSAGE_BYTES * 4 / 3) + 8);

/** Capability-scoped frames. A grant selects exactly one branch; gateways must
 * reject every other branch even when the wire shape itself is valid. */
export const DirectDataFrame = z.discriminatedUnion("type", [
  FrameBase.extend({ type: z.literal("terminal.input"), incarnation: z.number().int().positive(), producerId: z.string().regex(/^prod_[0-9abcdefghjkmnpqrstvwxyz]{16}$/), data: BoundedData }),
  FrameBase.extend({ type: z.literal("terminal.output"), incarnation: z.number().int().positive(), data: BoundedData }),
  FrameBase.extend({ type: z.literal("terminal.resize"), incarnation: z.number().int().positive(), cols: z.number().int().min(1).max(1_000), rows: z.number().int().min(1).max(1_000) }),
  FrameBase.extend({ type: z.literal("preview.request"), method: z.enum(["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE", "OPTIONS"]), path: z.string().startsWith("/").max(8_192), data: BoundedData.optional(), final: z.boolean() }),
  FrameBase.extend({ type: z.literal("preview.response"), status: z.number().int().min(100).max(599), data: BoundedData.optional(), final: z.boolean() }),
  FrameBase.extend({ type: z.literal("file.read"), offset: z.number().int().nonnegative(), length: z.number().int().positive().max(DIRECT_MAX_MESSAGE_BYTES) }),
  FrameBase.extend({ type: z.literal("file.chunk"), offset: z.number().int().nonnegative(), data: BoundedData, final: z.boolean() }),
  FrameBase.extend({ type: z.literal("file.write"), offset: z.number().int().nonnegative(), data: BoundedData, final: z.boolean() }),
  FrameBase.extend({
    type: z.literal("direct.ack"),
    /** Omitted on the initial credit-only frame. Sequence zero is valid
     * output, so treating the initial window grant as ACK(0) would skip it. */
    acknowledgedSequence: z.number().int().nonnegative().optional(),
    /** Receiver-owned replenishment; omitted on input ACKs. */
    creditBytes: z.number().int().nonnegative().max(DIRECT_FLOW_WINDOW_BYTES).optional(),
  }),
  z.object({ type: z.literal("direct.close"), code: z.string().min(1).max(80) }),
]);
export type DirectDataFrame = z.infer<typeof DirectDataFrame>;

const CLIENT_FRAME_TYPES = new Set<DirectDataFrame["type"]>([
  "terminal.input", "terminal.resize", "preview.request", "file.read", "file.write", "direct.ack", "direct.close",
]);
const SERVER_FRAME_TYPES = new Set<DirectDataFrame["type"]>([
  "terminal.output", "preview.response", "file.chunk", "direct.ack", "direct.close",
]);

export function isDirectClientFrame(frame: DirectDataFrame): boolean {
  return CLIENT_FRAME_TYPES.has(frame.type);
}

export function isDirectServerFrame(frame: DirectDataFrame): boolean {
  return SERVER_FRAME_TYPES.has(frame.type);
}

export function directFrameFeature(frame: DirectDataFrame): DirectTransportFeature | null {
  if (frame.type.startsWith("terminal.")) return "terminal";
  if (frame.type.startsWith("preview.")) return "preview";
  if (frame.type.startsWith("file.")) return "file";
  return null;
}

export function directScopeFeature(scope: DirectTicketScope): DirectTransportFeature {
  return scope.kind;
}

/** Any audience a ticket may be issued for: a Tailscale HTTPS endpoint or the
 * worker's own loopback listener. */
export function isDirectAudience(value: string): boolean {
  return isTailscaleAudience(value) || isLoopbackAudience(value);
}

/** `http://127.0.0.1:<port>/v1/direct`, nothing else: no hostname (so no DNS
 * rebinding), no credentials, no query, and an explicit port because the
 * gateway binds an ephemeral one. */
export function isLoopbackAudience(value: string): boolean {
  try {
    const url = new URL(value);
    const port = Number(url.port);
    return url.protocol === "http:"
      && url.username === ""
      && url.password === ""
      && url.hostname === "127.0.0.1"
      && url.port !== ""
      && Number.isInteger(port) && port >= 1 && port <= 65_535
      && url.pathname === "/v1/direct"
      && url.search === ""
      && url.hash === "";
  } catch {
    return false;
  }
}

export function isTailscaleAudience(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:"
      && url.username === ""
      && url.password === ""
      && url.port === ""
      && url.pathname === "/v1/direct"
      && url.search === ""
      && url.hash === ""
      && isSafeDnsName(url.hostname)
      && url.hostname.toLowerCase().endsWith(".ts.net");
  } catch {
    return false;
  }
}

export function isSafeDnsName(value: string): boolean {
  const name = value.endsWith(".") ? value.slice(0, -1) : value;
  return name.length > 0 && name.length <= 253
    && name.split(".").every((label) => /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/.test(label));
}

export function isSafeWorktreeRelativePath(value: string): boolean {
  if (value.startsWith("/") || value.includes("\\") || value.includes("\0")) return false;
  const parts = value.split("/");
  return parts.every((part) => part.length > 0 && part !== "." && part !== "..");
}

/** The LocalApi base URL (`http://127.0.0.1:<port>/v1/local`) of a running
 * loopback descriptor, else null. Host matching is the caller's job. */
export function localApiBaseUrl(descriptor: LoopbackTransportDescriptor | null | undefined): string | null {
  if (!descriptor || descriptor.state !== "running") return null;
  const localApi = descriptor.localApi;
  try {
    const url = new URL(descriptor.endpoint);
    return `${url.origin}${localApi.basePath}`;
  } catch {
    return null;
  }
}
